/**
 * Answers a trainer's questions about their own clients from data the home page already has
 * ("who hasn't replied?", "when does Ann train?", "who's on Monday?"). Deterministic: nothing is
 * sent to a model, and the text can only select what to show — it can't make the chat do anything.
 */
import type { Weekday } from "./index.ts";
import { DAYS as WEEKDAYS } from "./week.ts";

export interface KnowClient {
  id: string;
  name: string;
  sessionsPerWeek: number;
  whatsappOptIn?: boolean;
  preferences: { weekday: string; startTime?: string | null; endTime?: string | null }[];
}
export interface KnowWeek {
  label: "this week" | "next week";
  monday: Date;
  data?: {
    timezone: string;
    checkInRunLabel?: string;
    schedule: { status: string; checkInsSentAt: string | null; unplaced: unknown[] | null };
    sessions: { startsAt: string; endsAt: string; status: string; client: { id?: string; name: string } }[];
    checkIns: { status: string; client?: { id: string; name: string } }[];
  };
}
export interface Knowledge {
  clients: KnowClient[];
  weeks: KnowWeek[];
  now?: Date;
}

const DAY_WORD: Record<Weekday, RegExp> = {
  MON: /\bmon(day)?s?\b/,
  TUE: /\btue(s|sday)?s?\b/,
  WED: /\bwed(nesday)?s?\b/,
  THU: /\bthu(rs|rsday)?s?\b/,
  FRI: /\bfri(day)?s?\b/,
  SAT: /\bsat(urday)?s?\b/,
  SUN: /\bsun(day)?s?\b/,
};
const SHORT: Record<Weekday, string> = { MON: "Mon", TUE: "Tue", WED: "Wed", THU: "Thu", FRI: "Fri", SAT: "Sat", SUN: "Sun" };
const PARTS = { morning: [6 * 60, 12 * 60], lunch: [12 * 60, 14 * 60], afternoon: [12 * 60, 17 * 60], evening: [17 * 60, 21 * 60] } as const;
/** First names that are also everyday words: only matched when typed with a capital. */
const COMMON = new Set(["may", "will", "june", "april", "rose", "mark", "bill", "grace", "hope", "joy", "faith", "dawn", "summer", "art", "pat", "sue", "ray", "jack", "frank", "sky", "max"]);

const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const list = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function localParts(iso: string, tz: string) {
  const f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const p = Object.fromEntries(f.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { weekday: (p.weekday ?? "").slice(0, 3).toUpperCase() as Weekday, time: `${p.hour}:${p.minute}` };
}
const todayIn = (tz: string, now: Date) => localParts(now.toISOString(), tz).weekday;

function prefText(c: KnowClient): string {
  if (!c.preferences.length) return "no usual times set";
  return WEEKDAYS.filter((d) => c.preferences.some((p) => p.weekday === d))
    .map((d) => {
      const mine = c.preferences.filter((p) => p.weekday === d);
      return `${SHORT[d]} ${mine.some((p) => !p.startTime) ? "any time" : mine.map((p) => `${p.startTime}–${p.endTime}`).join(", ")}`;
    })
    .join(" · ");
}

/** Which clients the text names (full name, or first name when unambiguous enough). */
export function namedClients(text: string, clients: KnowClient[]): KnowClient[] {
  const lower = text.toLowerCase();
  return clients.filter((c) => {
    const full = c.name.toLowerCase().trim();
    const first = full.split(/\s+/)[0] ?? full;
    if (full.length > 2 && new RegExp(`\\b${escape(full)}\\b`).test(lower)) return true;
    if (first.length < 2) return false;
    if (COMMON.has(first)) return new RegExp(`\\b${escape(c.name.split(/\s+/)[0]!)}\\b`).test(text); // needs the capital
    return new RegExp(`\\b${escape(first)}('s)?\\b`).test(lower);
  });
}
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function weekFor(text: string, k: Knowledge, fallback: "this week" | "next week"): KnowWeek | undefined {
  const t = text.toLowerCase();
  const label = /\bnext week\b/.test(t) ? "next week" : /\b(this week|today|tomorrow|tonight)\b/.test(t) ? "this week" : fallback;
  return k.weeks.find((w) => w.label === label);
}

function aboutClient(c: KnowClient, k: Knowledge): string {
  const lines = [`${c.name} — ${plural(c.sessionsPerWeek, "session")} a week.`, `Usual times: ${prefText(c)}.`];
  for (const w of k.weeks) {
    if (!w.data) continue;
    const mine = w.data.sessions.filter((s) => (s.client.id ? s.client.id === c.id : s.client.name === c.name));
    const ci = w.data.checkIns.find((x) => x.client?.id === c.id);
    const label = w.label[0]!.toUpperCase() + w.label.slice(1);
    if (mine.length) {
      lines.push(`${label}: ${mine.map((s) => { const a = localParts(s.startsAt, w.data!.timezone); return `${SHORT[a.weekday]} ${a.time}${s.status === "PROPOSED" ? " (not published yet)" : ""}`; }).join(", ")}.`);
    } else if (ci && (ci.status === "PENDING" || ci.status === "IN_PROGRESS")) {
      lines.push(`${label}: not booked yet — hasn't finished replying to the check-in.`);
    } else lines.push(`${label}: nothing booked.`);
  }
  if (c.whatsappOptIn === false) lines.push("They've turned off WhatsApp messages, so RackUp can't check in with them.");
  return lines.join("\n");
}

/**
 * The answer, or null when the text isn't a question about clients (the chat then treats it as an
 * hours change, or explains what it can do).
 */
export function answerAboutClients(text: string, k: Knowledge): string | null {
  const t = text.toLowerCase().replace(/[’]/g, "'").trim();
  const now = k.now ?? new Date();

  // Actions on clients aren't done from the chat.
  if (/\b(delete|remove|add|archive|block|message|text|email|contact|invite|cancel)\b.*\bclients?\b/.test(t) || /\b(delete|remove|archive)\b/.test(t) && namedClients(text, k.clients).length) {
    return "I can't change clients from here — use the Clients page to add, edit or remove someone. I can tell you about them, though: try “who hasn't replied?” or “when does Ann train?”.";
  }

  const named = namedClients(text, k.clients);
  if (named.length && /\b(move|reschedule|book|swap|cancel|change)\b/.test(t)) {
    return `I can't move ${named[0]!.name.split(" ")[0]}'s sessions from here — open this week or next below to see sessions & replies. I can tell you when they train: ask “when does ${named[0]!.name.split(" ")[0]} train?”.`;
  }
  // An instruction about hours ("add sessions Monday mornings") is a change, not a question.
  if (!named.length && /^(please\s+)?(add|drop|remove|block|free up|open|close|make|set|change|move|swap|extend|shorten|i'?m|i am|i can'?t|i won'?t|can'?t|no)\b/.test(t)) return null;
  if (named.length) return named.slice(0, 3).map((c) => aboutClient(c, k)).join("\n\n") + (named.length > 3 ? `\n\n…and ${named.length - 3} more with that name.` : "");

  if (/\bhow many\b.*\b(clients?|people)\b/.test(t)) {
    const off = k.clients.filter((c) => c.whatsappOptIn === false).length;
    return `You have ${plural(k.clients.length, "client")}${off ? ` (${off} with WhatsApp messages turned off)` : ""}.`;
  }

  if (/\b(list|show|who are|all)\b.*\bclients\b|^(my )?clients\??$/.test(t)) {
    if (!k.clients.length) return "You don't have any clients yet. Share your sign-up link at the top of the page and they'll appear here.";
    const names = k.clients.map((c) => c.name).sort();
    return `Your clients: ${list(names.slice(0, 25))}${names.length > 25 ? ` and ${names.length - 25} more` : ""}.`;
  }

  const replyQ = /\b(repl|answer|respond|heard back|got back)/.test(t);
  if (replyQ) {
    const w = weekFor(t, k, "next week");
    if (!w?.data) return null;
    if (!w.data.schedule.checkInsSentAt && !w.data.checkIns.length) return `Check-ins for ${w.label} haven't gone out yet${w.data.checkInRunLabel ? ` — they go ${w.data.checkInRunLabel}` : ""}.`;
    const waiting = w.data.checkIns.filter((c) => c.status === "PENDING" || c.status === "IN_PROGRESS").map((c) => c.client?.name ?? "someone");
    const done = w.data.checkIns.filter((c) => c.status === "COMPLETED").map((c) => c.client?.name ?? "someone");
    const negative = /\b(not|n't|yet to|still to|haven|hasn|waiting|outstanding|missing)\b/.test(t);
    if (negative) return waiting.length ? `Still waiting on ${list(waiting)} for ${w.label}.` : `Everyone has replied for ${w.label}.`;
    return done.length ? `Replied for ${w.label}: ${list(done)}.${waiting.length ? ` Still waiting on ${list(waiting)}.` : ""}` : `Nobody has replied for ${w.label} yet.`;
  }

  if (/\b(unplaced|not placed|couldn'?t (fit|place)|didn'?t fit|left out|no slot)\b/.test(t)) {
    const w = weekFor(t, k, "next week");
    const un = (w?.data?.schedule.unplaced ?? []) as { name?: string }[];
    return un.length ? `Not placed ${w!.label}: ${list(un.map((u) => u.name ?? "a client"))}.` : `Everyone fits ${w?.label ?? "next week"} so far.`;
  }

  // Preferences: "who prefers mornings", "who can do Saturday".
  const prefQ = /\b(prefer|like|want|can do|can train|usually|available|free)/.test(t) && /\bwho\b|\bwhich\b|\banyone\b/.test(t);
  const days = WEEKDAYS.filter((d) => DAY_WORD[d].test(t));
  const part = (Object.keys(PARTS) as (keyof typeof PARTS)[]).find((p) => new RegExp(`\\b${p}s?\\b`).test(t));
  if (prefQ && (days.length || part)) {
    const hits = k.clients.filter((c) =>
      c.preferences.some((p) => {
        if (days.length && !days.includes(p.weekday as Weekday)) return false;
        if (!part) return true;
        if (!p.startTime || !p.endTime) return true;
        const [a, b] = PARTS[part];
        return toMin(p.startTime) < b && a < toMin(p.endTime);
      }),
    );
    const what = [days.map((d) => SHORT[d]).join("/"), part ? `${part}s` : ""].filter(Boolean).join(" ");
    return hits.length ? `Usually free ${what}: ${list(hits.map((c) => c.name))}.` : `None of your clients have ${what} in their usual times.`;
  }

  // Bookings: "who's on Monday?", "who am I training today?", "sessions next week".
  const bookingQ = /\b(who'?s|who is|who am i|training|booked|sessions?|on today|on tomorrow|schedule|diary|timetable)\b/.test(t);
  if (bookingQ) {
    const w = weekFor(t, k, "this week");
    if (!w?.data) return null;
    const tz = w.data.timezone;
    let want = days;
    if (/\btoday|tonight\b/.test(t)) want = [todayIn(tz, now)];
    if (/\btomorrow\b/.test(t)) want = [WEEKDAYS[(WEEKDAYS.indexOf(todayIn(tz, now)) + 1) % 7]!];
    const rows = w.data.sessions
      .map((s) => ({ s, at: localParts(s.startsAt, tz) }))
      .filter((x) => !want.length || want.includes(x.at.weekday))
      .sort((a, b) => a.s.startsAt.localeCompare(b.s.startsAt));
    const when = want.length === 1 && /\btoday|tonight\b/.test(t) ? "today" : want.length === 1 && /\btomorrow\b/.test(t) ? "tomorrow" : want.length ? `${w.label}, ${want.map((d) => SHORT[d]).join("/")}` : w.label;
    if (!rows.length) {
      const st = w.data.schedule.status;
      return `Nothing booked ${when}.${st === "PLANNING" || st === "COLLECTING" ? ` ${w.label[0]!.toUpperCase() + w.label.slice(1)} isn't built yet.` : ""}`;
    }
    return `Booked ${when}:\n${rows.map((x) => `• ${SHORT[x.at.weekday]} ${x.at.time} ${x.s.client.name}${x.s.status === "PROPOSED" ? " (not published yet)" : ""}`).join("\n")}`;
  }

  if (/\bclients?\b/.test(t)) return `You have ${plural(k.clients.length, "client")}. Ask me things like “who hasn't replied?”, “who's on Monday?” or “when does ${k.clients[0]?.name.split(" ")[0] ?? "Ann"} train?”.`;
  return null;
}

/** True when the text plausibly describes a change to hours (so it's worth interpreting). */
export function looksLikeHours(text: string): boolean {
  const t = text.toLowerCase();
  const time = /\b\d{1,2}([:.]\d{2})?\s*(am|pm)?\b|\b(morning|afternoon|evening|lunch|noon|midday|night)s?\b|\ball day\b|\bany time\b/.test(t);
  const day = WEEKDAYS.some((d) => DAY_WORD[d].test(t)) || /\b(week ?days?|week ?ends?|every ?day|daily)\b/.test(t);
  const verb = /\b(add|drop|remove|block|free|off|available|open|close|start|finish|work|working|hours|instead|change|move|swap|extend|shorten|no longer|can'?t|cannot|away|holiday)\b/.test(t);
  return (time && (day || verb)) || (day && verb);
}
